import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import CorporateCustomerDetailsModel from '../CorporateCustomerDetailsModel';
import {
  AMOUNT_INPUT_VALUE_TYPE,
  APPROVAL_STATUS,
  CUSTOMER_STATUSES,
  EXTERNAL_KYC_LINK,
  INDUSTRY_TYPES,
  KYC_PROVIDERS,
  ONBOARDING_STATUS,
  POP_UP_TYPE,
  RISK_PROFILE,
  YES_OR_NO
} from 'components/constants';
import countries from 'components/countries';
import i18nContext from 'components/i18n-context';
import OperatorsAsyncSelect from 'components/OperatorsAsyncSelect';
import { PopConfirmationScheme } from 'components/PopUpScheme/PopConfirmationScheme';
import { useApplicationContext } from 'contexts/ApplicationContext';
import { ACCOUNT_STATUS_UPDATE, CORPORATE_ACCOUNT_DETAILS_UPDATE, FULL_ACCESS, hasRole } from 'services/roles';
import {
  convertDate,
  formatDateTime,
  countryCodeRegExp,
  emailRegExp,
  parseExternalKycState,
  regularFieldRegExp
} from 'services/utils';
import AmountInput from 'uikit/AmountInput';
import Button from 'uikit/Button/Button';
import { CustomDatePicker } from 'uikit/CustomDatePicker';
import Input from 'uikit/Input/Input';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';
import { InputDropDownSearch } from 'uikit/InputDropDown/InputDropDownSearch';
import { PopUp } from 'uikit/PopUp/PopUp';
import { CheckBox } from 'uikit/CheckBox/CheckBox';
import './CorporateCustomerDetailsTab.scss';

const popUpInitialData = {
  isShowPopUp: false,
  popUpType: '',
  title: '',
  description: '',
  confirmButtonLabel: '',
  cancelButtonLabel: '',
  onCancel: () => {},
  onConfirm: () => {}
};

const CorporateCustomerDetailsTab = ({
  customerDetails,
  fieldsErrors,
  companyInfo,
  handleUpdateCustomerDetails,
  onChangeCustomerStatus,
  onChangeOnboardingStatus,
  onSaveEmail,
  onSaveKycStatus,
  onChangeExternalKycReference,
  onSaveRiskProfile,
  onSaveMlroApproval,
  onSaveDirectorApproval,
  onSaveNextAMLCheckDate,
  onReceiveKycData,
  parseFullAddress
}) => {
  const i18n = useContext(i18nContext);
  const { constants, configuration } = useApplicationContext();
  const [customerStatus, setCustomerStatus] = useState(customerDetails?.status || '');
  const [onboardingStatus, setOnboardingStatus] = useState(customerDetails?.onboardingStatus || '');
  const [email, setEmail] = useState(customerDetails?.email || '');
  const [kycExternalReference, setKycExternalReference] = useState(customerDetails?.kycExternalReference || '');
  const [kycStatus, setKycStatus] = useState(customerDetails?.kyc_status || '');
  const [riskProfile, setRiskProfile] = useState(customerDetails?.risk_profile || '');
  const [nextAMLCheckDate, setNextAMLCheckDate] = useState(
    customerDetails.next_aml_check_date ? new Date(customerDetails.next_aml_check_date) : ''
  );
  const [mlroApproval, setMlroApproval] = useState(customerDetails?.mlro_approval || '');
  const [directorApproval, setDirectorApproval] = useState(customerDetails?.director_approval || '');
  const [popUpData, setPopUpData] = useState(popUpInitialData);

  const customerStatusOptions = constants?.accountStatuses?.map((value) => ({
    key: value,
    value: i18n.getMessage(`customers.status.${value}`)
  })) || [];

  const onboardingStatusOptions = Object.keys(ONBOARDING_STATUS).map((status) => {
    return {
      key: ONBOARDING_STATUS[status],
      value: i18n.getMessage('onboardingStatus.' + ONBOARDING_STATUS[status])
    };
  });

  const countriesOptions = Object.values(countries).map((value) => ({
    key: value,
    value: i18n.getMessage(value)
  }));

  const kycStatusOptions = constants?.amlStatuses?.map((value) => ({
    key: value,
    value: i18n.getMessage(`kyc.status.${value}`)
  })) || [];

  const riskProfileOptions = Object.values(RISK_PROFILE).map((value) => ({
    key: value,
    value: i18n.getMessage(`customers.riskProfile.${value}`)
  }));

  const yesOrNoOptions = Object.values(YES_OR_NO).map((value) => ({
    key: value,
    value: i18n.getMessage(`customers.yesOrNoSelect.${value}`)
  }));

  const approvalOptions = Object.values(APPROVAL_STATUS).map((value) => ({
    key: value,
    value: i18n.getMessage(`customers.approvalStatus.${value}`)
  }));

  const legalEntityTypesOptions = constants?.legalEntityTypes?.map((value) => ({
    key: value,
    value: i18n.getMessage(`legalEntityType.${value}`)
  })) || [];

  const industryTypesOptions = Object.values(INDUSTRY_TYPES).map((value) => ({
    key: value,
    value: i18n.getMessage(`industryType.${value}`)
  }));

  const requiredFieldErrorMessage = i18n.getMessage('customer.validation.isRequired');
  const invalidFormatErrorMessage = i18n.getMessage('customer.validation.invalidFormat');
  const invalidCountryCodeErrorMessage = i18n.getMessage('customer.validation.invalidCountryCode');

  const otherAddressValidationSchema = Yup.object().shape({
    fullAddress: Yup.string()
      .nullable()
      .matches(regularFieldRegExp, invalidFormatErrorMessage)
      .max(255, i18n.getMessage('customer.validation.maxSize', { size: 255 })),
    address1: Yup.string()
      .nullable()
      .matches(regularFieldRegExp, invalidFormatErrorMessage)
      .max(200, i18n.getMessage('customer.validation.maxSize', { size: 200 })),
    /*address2: Yup.string()
      .nullable()
      .matches(regularFieldRegExp, invalidFormatErrorMessage)
      .max(50, i18n.getMessage('customer.validation.maxSize', { size: 50 })),*/
    zipCode: Yup.string()
      .nullable()
      .matches(regularFieldRegExp, invalidFormatErrorMessage)
      .max(10, i18n.getMessage('customer.validation.maxSize', { size: 10 })),
    city: Yup.string()
      .nullable()
      .matches(regularFieldRegExp, invalidFormatErrorMessage)
      .max(50, i18n.getMessage('customer.validation.maxSize', { size: 50 })),
    country: Yup.string().nullable().length(2).matches(countryCodeRegExp, invalidCountryCodeErrorMessage).uppercase()
  });

  const verifiedAddressValidationSchema = Yup.object().shape({
    fullAddress: Yup.string()
      .required(requiredFieldErrorMessage)
      .matches(regularFieldRegExp, invalidFormatErrorMessage)
      .max(255, i18n.getMessage('customer.validation.maxSize', { size: 255 })),
    address1: Yup.string()
      .required(requiredFieldErrorMessage)
      .matches(regularFieldRegExp, invalidFormatErrorMessage)
      .max(200, i18n.getMessage('customer.validation.maxSize', { size: 200 })),
    /*address2: Yup.string()
      .required(requiredFieldErrorMessage)
      .matches(regularFieldRegExp, invalidFormatErrorMessage)
      .max(50, i18n.getMessage('customer.validation.maxSize', { size: 50 })),*/
    zipCode: Yup.string()
      .required(requiredFieldErrorMessage)
      .matches(regularFieldRegExp, invalidFormatErrorMessage)
      .max(10, i18n.getMessage('customer.validation.maxSize', { size: 10 })),
    city: Yup.string()
      .required(requiredFieldErrorMessage)
      .matches(regularFieldRegExp, invalidFormatErrorMessage)
      .max(50, i18n.getMessage('customer.validation.maxSize', { size: 50 })),
    country: Yup.string()
      .required(requiredFieldErrorMessage)
      .length(2)
      .matches(countryCodeRegExp, invalidCountryCodeErrorMessage)
      .uppercase()
  });

  const getAddressValidationSchema = () => {
    return customerDetails?.status === CUSTOMER_STATUSES.VERIFIED
      ? verifiedAddressValidationSchema
      : otherAddressValidationSchema;
  };

  const validationSchema = Yup.object().shape({
    source: Yup.string()
      .nullable()
      .matches(regularFieldRegExp, invalidFormatErrorMessage)
      .max(250, i18n.getMessage('customer.validation.maxSize', { size: 250 })),
    companyName: Yup.string()
      .required(requiredFieldErrorMessage)
      .matches(regularFieldRegExp, invalidFormatErrorMessage)
      .max(50, i18n.getMessage('customer.validation.maxSize', { size: 50 })),
    registrationNumber: Yup.string().when('customerStatus', {
      is: CUSTOMER_STATUSES.VERIFIED,
      then: (schema) =>
        schema
          .matches(regularFieldRegExp, invalidFormatErrorMessage)
          .required(requiredFieldErrorMessage)
          .max(20, i18n.getMessage('customer.validation.maxSize', { size: 20 })),
      otherwise: (schema) => schema.nullable().max(20, i18n.getMessage('customer.validation.maxSize', { size: 20 }))
    }),
    jurisdictionCode: Yup.string().when('customerStatus', {
      is: CUSTOMER_STATUSES.VERIFIED,
      then: (schema) => schema.max(10, i18n.getMessage('customer.validation.maxSize', { size: 10 })),
      otherwise: (schema) => schema.nullable().max(10, i18n.getMessage('customer.validation.maxSize', { size: 10 }))
    }),
    entityLegalType: Yup.string().when('customerStatus', {
      is: CUSTOMER_STATUSES.VERIFIED,
      then: (schema) => schema.required(requiredFieldErrorMessage),
      otherwise: (schema) => schema.nullable()
    }),
    registrationDate: Yup.date().when('customerStatus', {
      is: CUSTOMER_STATUSES.VERIFIED,
      then: (schema) => schema.required(requiredFieldErrorMessage),
      otherwise: (schema) => schema.nullable()
    }),
    hideBusinessAddress: Yup.boolean(),
    legalAddress: getAddressValidationSchema(),
    businessAddress: Yup.object().when('hideBusinessAddress', {
      is: false,
      then: () => getAddressValidationSchema(),
      otherwise: (schema) => schema.nullable()
    }),
    webSite: Yup.string().when('customerStatus', {
      is: CUSTOMER_STATUSES.VERIFIED,
      then: (schema) =>
        schema
          .matches(regularFieldRegExp, invalidFormatErrorMessage)
          .max(50, i18n.getMessage('customer.validation.maxSize', { size: 50 })),
      otherwise: (schema) =>
        schema
          .nullable()
          .matches(regularFieldRegExp, invalidFormatErrorMessage)
          .max(50, i18n.getMessage('customer.validation.maxSize', { size: 50 }))
    }),
    businessNature: Yup.string().when('customerStatus', {
      is: CUSTOMER_STATUSES.VERIFIED,
      then: (schema) =>
        schema
          .matches(regularFieldRegExp, invalidFormatErrorMessage)
          .max(200, i18n.getMessage('customer.validation.maxSize', { size: 200 })),
      otherwise: (schema) =>
        schema
          .nullable()
          .matches(regularFieldRegExp, invalidFormatErrorMessage)
          .max(200, i18n.getMessage('customer.validation.maxSize', { size: 200 }))
    }),
    declaredAccountTurnover: Yup.string()
      .nullable()
      .matches(regularFieldRegExp, invalidFormatErrorMessage)
      .max(10, i18n.getMessage('customer.validation.maxSize', { size: 10 })),
    nextAMLCheckDate: Yup.date().nullable(),
    industryType: Yup.string().when('customerStatus', {
      is: CUSTOMER_STATUSES.VERIFIED,
      then: (schema) => schema.required(requiredFieldErrorMessage),
      otherwise: (schema) => schema.nullable()
    }),
    accountOpeningPurpose: Yup.string().when('customerStatus', {
      is: CUSTOMER_STATUSES.VERIFIED,
      then: (schema) =>
        schema
          .required(requiredFieldErrorMessage)
          .max(255, i18n.getMessage('customer.validation.maxSize', { size: 255 })),
      otherwise: (schema) => schema.nullable().max(255, i18n.getMessage('customer.validation.maxSize', { size: 255 }))
    }),
    accountClosingReason: Yup.string().when('customerStatus', {
      is: CUSTOMER_STATUSES.CLOSED,
      then: (schema) =>
        schema
          .required(requiredFieldErrorMessage)
          .max(255, i18n.getMessage('customer.validation.maxSize', { size: 255 })),
      otherwise: (schema) => schema.nullable().max(255, i18n.getMessage('customer.validation.maxSize', { size: 255 }))
    }),
    customerOwner: Yup.object().when('customerStatus', {
      is: CUSTOMER_STATUSES.VERIFIED,
      then: (schema) => schema.required(requiredFieldErrorMessage),
      otherwise: (schema) => schema.nullable()
    }),
    agentName: Yup.string()
      .nullable()
      .max(255, i18n.getMessage('customer.validation.maxSize', { size: 255 })),
    contactPhone: Yup.string()
      .nullable()
      .required(requiredFieldErrorMessage)
      .max(25, i18n.getMessage('customer.validation.maxSize', { size: 25 })),
    contactEmail: Yup.string()
      .nullable()
      .required(requiredFieldErrorMessage)
      .max(50, i18n.getMessage('customer.validation.maxSize', { size: 50 }))
      .trim()
      .matches(emailRegExp, { message: i18n.getMessage('customer.validation.invalidEmail') }),
    googleDriveLink: Yup.string()
      .nullable()
      .max(255, i18n.getMessage('customer.validation.maxSize', { size: 255 })),
    internalAmlDocumentsLink: Yup.string()
      .nullable()
      .max(255, i18n.getMessage('customer.validation.maxSize', { size: 255 }))
  });

  const form = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      customerStatus: customerDetails?.status || '',
      source: customerDetails.source,
      companyName: companyInfo.companyName,
      registrationNumber: companyInfo.registrationNumber,
      jurisdictionCode: companyInfo.jurisdictionCode,
      entityLegalType: companyInfo.entityLegalType || null,
      registrationDate: companyInfo.registrationDate ? new Date(companyInfo.registrationDate) : '',
      legalAddress: CorporateCustomerDetailsModel.address(customerDetails.legal_address || {}),
      businessAddress: CorporateCustomerDetailsModel.address(customerDetails.business_address || {}),
      webSite: companyInfo.webSite,
      businessNature: companyInfo.businessNature,
      declaredAccountTurnover: customerDetails.declaredAccountTurnover,
      partners: customerDetails.partners,
      additionalComments: customerDetails.additionalComments,
      commentToDocuments: customerDetails.commentDocuments,
      licence: customerDetails.licence,
      sanctionsRelated: customerDetails.sanctions_related,
      adverseMediaInvolved: customerDetails.adverse_media_involved,
      industryType: customerDetails.industry_type,
      accountOpeningPurpose: customerDetails.accountOpeningPurpose,
      accountClosingReason: customerDetails.accountClosingReason,
      customerOwner: customerDetails?.customer_owner || '',
      agentName: customerDetails?.agent_name || '',
      contactPhone: customerDetails.contactPhone || '',
      contactEmail: customerDetails.contactEmail || customerDetails.email || '',
      googleDriveLink: customerDetails.googleDriveLink || '',
      internalAmlDocumentsLink: customerDetails.internalAmlDocumentsLink || '',
      hideBusinessAddress: customerDetails?.hide_legal_address || false
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const businessAddress = values.hideBusinessAddress
        ? CorporateCustomerDetailsModel.addressToServer(values.legalAddress)
        : CorporateCustomerDetailsModel.addressToServer(values.businessAddress);

      handleUpdateCustomerDetails({
        ...CorporateCustomerDetailsModel.commonInformationToServer(values),
        business_address: businessAddress,
        legal_address: CorporateCustomerDetailsModel.addressToServer(values.legalAddress),
        hide_legal_address: values.hideBusinessAddress,
        declaredAccountTurnover: values.declaredAccountTurnover ? values.declaredAccountTurnover : null,
        partners: values.partners ? values.partners : null,
        additionalComments: values.additionalComments ? values.additionalComments : null,
        commentToDocuments: values.commentDocuments ? values.commentDocuments : null,
        licence: values.licence ? values.licence : null,
        source: values.source ? values.source : null,
        sanctionsRelated: values.sanctionsRelated ? values.sanctionsRelated : null,
        adverseMediaInvolved: values.adverseMediaInvolved ? values.adverseMediaInvolved : null,
        customerOwner: values.customerOwner ? values.customerOwner.id : null,
        agentName: values.agentName ? values.agentName : null,
        contactEmail: values.contactEmail ? values.contactEmail : null,
        contactPhone: values.contactPhone ? values.contactPhone : null,
        googleDriveLink: values.googleDriveLink ? values.googleDriveLink : null,
        internalAmlDocumentsLink: values.internalAmlDocumentsLink ? values.internalAmlDocumentsLink : null
      });
    }
  });

  const { values, errors, handleSubmit, handleChange, setFieldValue, setErrors, setValues, validateForm, resetForm } =
    form;
  const [formValues, setFormValues] = useState(null);

  useEffect(() => {
    if (formValues) {
      setValues(formValues);
    }
    setCustomerStatus(customerDetails?.status || '');
    setFieldValue('customerStatus', customerDetails?.status || '');
    setErrors({});
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerDetails?.status]);

  useEffect(() => {
    setOnboardingStatus(customerDetails?.onboardingStatus || '');
  }, [customerDetails?.onboardingStatus]);

  useEffect(() => {
    setEmail(customerDetails?.email || '');
  }, [customerDetails?.email]);

  useEffect(() => {
    setKycExternalReference(customerDetails?.kycExternalReference || '');
  }, [customerDetails?.kycExternalReference]);

  useEffect(() => {
    setKycStatus(customerDetails?.kyc_status || '');
  }, [customerDetails?.kyc_status]);

  useEffect(() => {
    setRiskProfile(customerDetails?.risk_profile || '');
  }, [customerDetails?.risk_profile]);

  useEffect(() => {
    setNextAMLCheckDate(customerDetails.next_aml_check_date ? new Date(customerDetails.next_aml_check_date) : '');
  }, [customerDetails.next_aml_check_date]);

  useEffect(() => {
    setMlroApproval(customerDetails?.mlro_approval || '');
  }, [customerDetails?.mlro_approval]);

  useEffect(() => {
    setDirectorApproval(customerDetails?.director_approval || '');
  }, [customerDetails?.director_approval]);

  const firstCreatedAccount =
    customerDetails?.wallets && customerDetails?.wallets?.length
      ? customerDetails?.wallets.sort((a, b) => a.created_at - b.created_at)[0]
      : undefined;
  const externalKycState = parseExternalKycState(customerDetails.kyc_external_state);

  //TODO: What is the block and how it need to be display
  /// const payment = this.props.data.payment;
  // const {
  //   companyInfo,
  //   legalAddress,
  //   businessAddress,
  //   incomingPartners,
  //   outgoingPartners,
  //   turnovers
  // } = this.state;
  //TODO: What is the block and how it need to be display

  const scrollToError = () => {
    const errorElement = document.querySelector('.error');
    if (errorElement) {
      const yOffset = -50;
      const y = errorElement.getBoundingClientRect().top + window.pageYOffset + yOffset;

      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const handleChangeEmail = (e) => {
    setEmail(e.target.value);
  };

  const handleChangeKycExternalReference = (e) => {
    setKycExternalReference(e.target.value);
  };

  const handleChangeCustomerStatus = async () => {
    setFormValues(values);
    await onChangeCustomerStatus(customerStatus);
  };

  const resetPopUpData = () => {
    setPopUpData(popUpInitialData);
  };

  const handleConfirmReceiveKycData = async () => {
    setPopUpData({
      isShowPopUp: true,
      popUpType: POP_UP_TYPE.CONFIRMATION,
      title: i18n.getMessage('customers.corporate.customerDetailsTab.confirmReceiveKycDataModal.title'),
      description: i18n.getMessage('customers.corporate.customerDetailsTab.confirmReceiveKycDataModal.message'),
      confirmButtonLabel: i18n.getMessage(
        'customers.corporate.customerDetailsTab.confirmReceiveKycDataModal.button.confirm'
      ),
      cancelButtonLabel: i18n.getMessage(
        'customers.corporate.customerDetailsTab.confirmReceiveKycDataModal.button.cancel'
      ),
      onConfirm: async () => {
        await onReceiveKycData();
        resetPopUpData();
      },
      onCancel: resetPopUpData
    });
  };

  const handleHideBusinessAddressChange = () => {
    const isChecked = values.hideBusinessAddress;

    setFieldValue('hideBusinessAddress', !isChecked);

    if (isChecked) {
      setFieldValue('businessAddress', values.legalAddress);
    }
  };

  return (
    <div className={'customer-details-table'}>
      <div style={{ position: 'relative' }}></div>
      <form
        action={'/form'}
        autoComplete={'off'}
        id={'send'}
        onSubmit={handleSubmit}
        className={'customer-details-form'}
      >
        <table className={'table-wrapper'}>
          <tbody className={'table-body'}>
            <tr className={'table-row'}>
              <td colSpan={'2'} className={'table-cell subsection'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.subsection.customerDetails')}
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.customerStatus')}
              </td>
              <td className={'table-cell'}>
                <div className={'input-with-save-button-wrapper'}>
                  <InputDropDown
                    className={'table-cell-input'}
                    id={'customerStatus'}
                    name={'customerStatus'}
                    options={customerStatusOptions}
                    value={customerStatus}
                    onChange={(name, value) => setCustomerStatus(value)}
                    isDisabled={!hasRole(ACCOUNT_STATUS_UPDATE)}
                  />
                  <Button
                    className={'customer-details-tab-button'}
                    size={'small'}
                    type={'primary'}
                    onClick={handleChangeCustomerStatus}
                    isDisabled={!hasRole(ACCOUNT_STATUS_UPDATE)}
                  >
                    {i18n.getMessage('customers.corporate.customerDetailsTab.button.save')}
                  </Button>
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.onboardingStatus')}
              </td>
              <td className={'table-cell'}>
                <div className={'input-with-save-button-wrapper'}>
                  <InputDropDown
                    className={'table-cell-input'}
                    id={'onboardingStatus'}
                    name={'onboardingStatus'}
                    options={onboardingStatusOptions}
                    value={onboardingStatus}
                    onChange={(name, value) => setOnboardingStatus(value)}
                  />
                  <Button
                    className={'customer-details-tab-button'}
                    size={'small'}
                    type={'primary'}
                    onClick={() => onChangeOnboardingStatus(onboardingStatus)}
                  >
                    {i18n.getMessage('customers.corporate.customerDetailsTab.button.save')}
                  </Button>
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.corporate.customerDetailsTab.customerId')}</td>
              <td className={'table-cell'}>
                <span className={'table-cell-span'}>{customerDetails.account_number}</span>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.customerEmail')}
              </td>
              <td className={'table-cell'}>
                <div className={'input-with-save-button-wrapper'}>
                  <Input className={'table-cell-input'} name={'email'} value={email} onChange={handleChangeEmail} />
                  <Button
                    className={'customer-details-tab-button'}
                    size={'small'}
                    type={'primary'}
                    onClick={() => onSaveEmail(email)}
                  >
                    {i18n.getMessage('customers.corporate.customerDetailsTab.button.save')}
                  </Button>
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.customerRegistrationTime')}
              </td>
              <td className={'table-cell'}>
                <div className={'table-cell-span-wrapper'}>
                  <span className={'table-cell-span'}>{formatDateTime(customerDetails?.user?.registration_date)}</span>
                  <span className={'table-cell-span'}>IP: {customerDetails?.user?.registration_ip}</span>
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.firstAccountOpeningDate')}
              </td>
              <td className={'table-cell'}>
                <span className={'table-cell-span'}>
                  {firstCreatedAccount ? formatDateTime(firstCreatedAccount.created_at) : ''}
                </span>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.customerOwner')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <OperatorsAsyncSelect
                    handleValueChanged={(value) => setFieldValue('customerOwner', value)}
                    value={values.customerOwner}
                    error={errors.customerOwner}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.customerSource')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'source'}
                    value={values.source}
                    onChange={handleChange}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.source || fieldsErrors?.source}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.corporate.customerDetailsTab.agentName')}</td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'agentName'}
                    value={values.agentName}
                    onChange={handleChange}
                    error={errors?.agentName || fieldsErrors?.agentName}
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <table className={'table-wrapper'}>
          <tbody className={'table-body'}>
            <tr className={'table-row'}>
              <td colSpan={'2'} className={'table-cell subsection'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.subsection.customerContactDetails')}
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.customerContactEmailAddress')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'contactEmail'}
                    value={values.contactEmail}
                    onChange={handleChange}
                    error={errors?.contactEmail || fieldsErrors?.contactEmail}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.customerContactPhoneNumber')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'contactPhone'}
                    value={values.contactPhone}
                    onChange={handleChange}
                    error={errors?.contactPhone || fieldsErrors?.contactPhone}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        {hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE) && (
          <div className={'bottom-actions-wrapper'}>
            <div className={'changes-actions-wrapper'}>
              <Button
                className={'customers-button'}
                size={'medium'}
                type={'primary'}
                onClick={handleConfirmReceiveKycData}
              >
                {i18n.getMessage('customers.corporate.customerDetailsTab.button.receiveKycData')}
              </Button>
            </div>
          </div>
        )}

        <table className={'table-wrapper'}>
          <tbody className={'table-body'}>
            <tr className={'table-row'}>
              <td colSpan={'2'} className={'table-cell subsection'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.subsection.externalKycProvider')}
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.kycExternalReference')}
              </td>
              <td className={'table-cell'}>
                <div className={'input-with-save-button-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'kycExternalReference'}
                    value={kycExternalReference}
                    onChange={handleChangeKycExternalReference}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                    error={fieldsErrors?.kycExternalReference}
                  />
                  <Button
                    className={'customer-details-tab-button'}
                    size={'small'}
                    type={'primary'}
                    onClick={() => onChangeExternalKycReference(kycExternalReference)}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                  >
                    {i18n.getMessage('customers.corporate.customerDetailsTab.button.save')}
                  </Button>
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.externalKycLink')}
              </td>
              <td className={'table-cell'}>
                {customerDetails?.verification_provider === KYC_PROVIDERS.SUMSUB && externalKycState.identifier && (
                  <span className={'table-cell-span'}>
                    <a
                      className={'link-normal'}
                      href={`${EXTERNAL_KYC_LINK}${externalKycState.identifier}`}
                      target={'_blank'}
                      rel={'noopener noreferrer'}
                    >
                      {`${EXTERNAL_KYC_LINK}${externalKycState.identifier}`}
                    </a>
                  </span>
                )}
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.corporate.customerDetailsTab.statuses')}</td>
              <td className={'table-cell'}>
                <span className={'table-cell-span'}>{externalKycState.statuses}</span>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.corporate.customerDetailsTab.tags')}</td>
              <td className={'table-cell'}>
                <span className={'table-cell-span'}>{externalKycState.tags}</span>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.linkToDocuments')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'googleDriveLink'}
                    value={values.googleDriveLink}
                    onChange={handleChange}
                    error={errors?.googleDriveLink || fieldsErrors?.googleDriveLink}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <table className={'table-wrapper'}>
          <tbody className={'table-body'}>
            <tr className={'table-row'}>
              <td colSpan={'2'} className={'table-cell subsection'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.subsection.companyDetails')}
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.corporate.customerDetailsTab.companyName')}</td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'companyName'}
                    value={values.companyName}
                    onChange={handleChange}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.companyName || fieldsErrors?.companyName}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.registrationNumber')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'registrationNumber'}
                    value={values.registrationNumber}
                    onChange={handleChange}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.registrationNumber || fieldsErrors?.registrationNumber}
                  />
                </div>
              </td>
            </tr>
            {configuration?.parametersByKey['finmatic.kyb.provider'] === KYC_PROVIDERS.SHUFTI && (
              <tr className={'table-row'}>
                <td className={'table-cell'}>
                  {i18n.getMessage('customers.corporate.customerDetailsTab.jurisdictionCode')}
                </td>
                <td className={'table-cell'}>
                  <div className={'customer-details-input-wrapper'}>
                    <Input
                      className={'table-cell-input'}
                      name={'jurisdictionCode'}
                      value={values.jurisdictionCode}
                      onChange={handleChange}
                      isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                      error={errors?.jurisdictionCode || fieldsErrors?.jurisdictionCode}
                    />
                  </div>
                </td>
              </tr>
            )}
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.corporate.customerDetailsTab.entityType')}</td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <InputDropDown
                    className={'table-cell-input'}
                    id={'entityLegalType'}
                    name={'entityLegalType'}
                    options={legalEntityTypesOptions}
                    value={values.entityLegalType || ''}
                    onChange={(name, value) => setFieldValue(name, value)}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.entityLegalType || fieldsErrors?.entityLegalType}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.registrationDate')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <CustomDatePicker
                    className={'table-cell-date-input'}
                    name={'registrationDate'}
                    value={values.registrationDate}
                    onChange={(name, value) => setFieldValue(name, value)}
                    error={errors?.registrationDate || fieldsErrors?.registrationDate}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.corporate.customerDetailsTab.webSite')}</td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'webSite'}
                    value={values.webSite}
                    onChange={handleChange}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.webSite || fieldsErrors?.webSite}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.businessNature')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'businessNature'}
                    value={values.businessNature}
                    onChange={handleChange}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.businessNature || fieldsErrors?.businessNature}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.corporate.customerDetailsTab.industryType')}</td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <InputDropDownSearch
                    className={'table-cell-input-dropdown-search'}
                    id={'industryType'}
                    name={'industryType'}
                    options={industryTypesOptions}
                    value={values.industryType}
                    onChange={(name, value) => setFieldValue(name, value)}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.industryType || fieldsErrors?.industryType}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.accountOpeningPurpose')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'accountOpeningPurpose'}
                    value={values.accountOpeningPurpose}
                    onChange={handleChange}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.accountOpeningPurpose || fieldsErrors?.accountOpeningPurpose}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.accountClosingReason')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'accountClosingReason'}
                    value={values.accountClosingReason}
                    onChange={handleChange}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.accountClosingReason || fieldsErrors?.accountClosingReason}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.declaredAccountTurnover')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <AmountInput
                    className={'table-cell-input'}
                    name={'declaredAccountTurnover'}
                    returnValueType={AMOUNT_INPUT_VALUE_TYPE.STRING}
                    value={values.declaredAccountTurnover}
                    onChange={handleChange}
                    placeholder={i18n.getMessage(
                      'customers.corporate.customerDetailsTab.declaredAccountTurnover.placeholder'
                    )}
                    error={errors?.declaredAccountTurnover || fieldsErrors?.declaredAccountTurnover}
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <table className={'table-wrapper'}>
          <tbody className={'table-body'}>
            <tr className={'table-row'}>
              <td colSpan={'2'} className={'table-cell subsection'}>
                <div className={'hide-address-wrapper'}>
                  {i18n.getMessage('customers.corporate.customerDetailsTab.subsection.addresses')}
                  <CheckBox
                    className={'hide-address-checkbox'}
                    name={'hideBusinessAddress'}
                    onChange={handleHideBusinessAddressChange}
                    disabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                    isActive={values.hideBusinessAddress}
                    isReverseView={false}
                    showCheckBox={true}
                    value={i18n.getMessage('customers.corporate.customerDetailsTab.hideBusinessAddress')}
                  />
                </div>
              </td>
            </tr>
            {/*<tr className={'table-row'}>*/}
            {/*  <td className={'table-cell'}>*/}
            {/*    {i18n.getMessage('customers.corporate.customerDetailsTab.hideBusinessAddress')}*/}
            {/*  </td>*/}
            {/*  <td className={'table-cell'}>*/}
            {/*    <div className={'customer-details-input-wrapper'}>*/}
            {/*      <CheckBox*/}
            {/*        name={'hideBusinessAddress'}*/}
            {/*        onChange={handleHideBusinessAddressChange}*/}
            {/*        isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}*/}
            {/*        isActive={values.hideBusinessAddress}*/}
            {/*      />*/}
            {/*    </div>*/}
            {/*  </td>*/}
            {/*</tr>*/}
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.legalFullAddress')}
              </td>
              <td className={'table-cell'}>
                <div className={'input-with-save-button-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'legalAddress.fullAddress'}
                    value={values?.legalAddress?.fullAddress}
                    onChange={handleChange}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.legalAddress?.fullAddress || fieldsErrors?.fullAddress}
                  />
                  <Button
                    className={'customer-details-tab-button parse-full-address-button'}
                    size={'small'}
                    type={'primary'}
                    onClick={async () => {
                      try {
                        const parsedAddress = await parseFullAddress(values?.legalAddress?.fullAddress);

                        if (parsedAddress) {
                          await setFieldValue('legalAddress', {
                            ...values.legalAddress,
                            address1: parsedAddress.address_1 || '',
                            address2: parsedAddress.address_2 || '',
                            zipCode: parsedAddress.zip_code || '',
                            city: parsedAddress.city || '',
                            country: parsedAddress.country || '',
                            fullAddress: parsedAddress.full_address || ''
                          });
                        }
                      } catch (error) {
                        // Error already handled in store
                      }
                    }}
                    isDisabled={!hasRole(FULL_ACCESS)}
                  >
                    {i18n.getMessage('customers.corporate.customerDetailsTab.button.parseAddress')}
                  </Button>
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.legalAddress1')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'legalAddress.address1'}
                    value={values?.legalAddress?.address1}
                    onChange={handleChange}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.legalAddress?.address1 || fieldsErrors?.address1}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.legalAddress2')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'legalAddress.address2'}
                    value={values?.legalAddress?.address2}
                    onChange={handleChange}
                    isDisabled={/*!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)*/ true}
                    error={errors?.legalAddress?.address2 || fieldsErrors?.address2}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.corporate.customerDetailsTab.legalZipCode')}</td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'legalAddress.zipCode'}
                    value={values?.legalAddress?.zipCode}
                    onChange={handleChange}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.legalAddress?.zipCode || fieldsErrors?.zipCode}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.corporate.customerDetailsTab.legalCity')}</td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'legalAddress.city'}
                    value={values?.legalAddress?.city}
                    onChange={handleChange}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.legalAddress?.city || fieldsErrors?.city}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.corporate.customerDetailsTab.legalCountry')}</td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <InputDropDownSearch
                    className={'table-cell-input-dropdown-search'}
                    id={'legalAddress.country'}
                    name={'legalAddress.country'}
                    options={countriesOptions}
                    value={values?.legalAddress?.country}
                    onChange={(name, value) => setFieldValue(name, value)}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.legalAddress?.country || fieldsErrors?.legalAddress?.country}
                  />
                </div>
              </td>
            </tr>
            {!values.hideBusinessAddress && (
              <>
                <tr className={'table-row'}>
                  <td className={'table-cell'}>
                    {i18n.getMessage('customers.corporate.customerDetailsTab.officeFullAddress')}
                  </td>
                  <td className={'table-cell'}>
                    <div className={'input-with-save-button-wrapper'}>
                      <Input
                        className={'table-cell-input'}
                        name={'businessAddress.fullAddress'}
                        value={values?.businessAddress?.fullAddress}
                        onChange={handleChange}
                        isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                        error={errors?.businessAddress?.fullAddress || fieldsErrors?.fullAddress}
                      />
                      <Button
                        className={'customer-details-tab-button parse-full-address-button'}
                        size={'small'}
                        type={'primary'}
                        onClick={async () => {
                          try {
                            const parsedAddress = await parseFullAddress(values?.businessAddress?.fullAddress);

                            if (parsedAddress) {
                              await setFieldValue('businessAddress', {
                                ...values.businessAddress,
                                address1: parsedAddress.address_1 || '',
                                address2: parsedAddress.address_2 || '',
                                zipCode: parsedAddress.zip_code || '',
                                city: parsedAddress.city || '',
                                country: parsedAddress.country || '',
                                fullAddress: parsedAddress.full_address || ''
                              });
                            }
                          } catch (error) {
                            // Error already handled in store
                          }
                        }}
                        isDisabled={!hasRole(FULL_ACCESS)}
                      >
                        {i18n.getMessage('customers.corporate.customerDetailsTab.button.parseAddress')}
                      </Button>
                    </div>
                  </td>
                </tr>
                <tr className={'table-row'}>
                  <td className={'table-cell'}>
                    {i18n.getMessage('customers.corporate.customerDetailsTab.officeAddress1')}
                  </td>
                  <td className={'table-cell'}>
                    <div className={'customer-details-input-wrapper'}>
                      <Input
                        className={'table-cell-input'}
                        name={'businessAddress.address1'}
                        value={values?.businessAddress?.address1}
                        onChange={handleChange}
                        isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                        error={errors?.businessAddress?.address1 || fieldsErrors?.address1}
                      />
                    </div>
                  </td>
                </tr>
                <tr className={'table-row'}>
                  <td className={'table-cell'}>
                    {i18n.getMessage('customers.corporate.customerDetailsTab.officeAddress2')}
                  </td>
                  <td className={'table-cell'}>
                    <div className={'customer-details-input-wrapper'}>
                      <Input
                        className={'table-cell-input'}
                        name={'businessAddress.address2'}
                        value={values?.businessAddress?.address2}
                        onChange={handleChange}
                        isDisabled={/*!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)*/ true}
                        error={errors?.businessAddress?.address2 || fieldsErrors?.address2}
                      />
                    </div>
                  </td>
                </tr>
                <tr className={'table-row'}>
                  <td className={'table-cell'}>
                    {i18n.getMessage('customers.corporate.customerDetailsTab.officeZipCode')}
                  </td>
                  <td className={'table-cell'}>
                    <div className={'customer-details-input-wrapper'}>
                      <Input
                        className={'table-cell-input'}
                        name={'businessAddress.zipCode'}
                        value={values?.businessAddress?.zipCode}
                        onChange={handleChange}
                        isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                        error={errors?.businessAddress?.zipCode || fieldsErrors?.zipCode}
                      />
                    </div>
                  </td>
                </tr>
                <tr className={'table-row'}>
                  <td className={'table-cell'}>
                    {i18n.getMessage('customers.corporate.customerDetailsTab.officeCity')}
                  </td>
                  <td className={'table-cell'}>
                    <div className={'customer-details-input-wrapper'}>
                      <Input
                        className={'table-cell-input'}
                        name={'businessAddress.city'}
                        value={values?.businessAddress?.city}
                        onChange={handleChange}
                        isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                        error={errors?.businessAddress?.city || fieldsErrors?.city}
                      />
                    </div>
                  </td>
                </tr>
                <tr className={'table-row'}>
                  <td className={'table-cell'}>
                    {i18n.getMessage('customers.corporate.customerDetailsTab.officeCountry')}
                  </td>
                  <td className={'table-cell'}>
                    <div className={'customer-details-input-wrapper'}>
                      <InputDropDownSearch
                        className={'table-cell-input-dropdown-search'}
                        id={'businessAddress.country'}
                        name={'businessAddress.country'}
                        options={countriesOptions}
                        value={values?.businessAddress?.country}
                        onChange={(name, value) => setFieldValue(name, value)}
                        isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                        error={errors?.businessAddress?.country || fieldsErrors?.businessAddress?.country}
                      />
                    </div>
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>

        <table className={'table-wrapper'}>
          <tbody className={'table-body'}>
            <tr className={'table-row'}>
              <td colSpan={'2'} className={'table-cell subsection'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.subsection.kyc')}
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.corporate.customerDetailsTab.kycStatus')}</td>
              <td className={'table-cell'}>
                <div className={'input-with-save-button-wrapper'}>
                  <InputDropDown
                    className={'table-cell-input'}
                    id={'kycStatus'}
                    name={'kycStatus'}
                    options={kycStatusOptions}
                    value={kycStatus}
                    onChange={(name, value) => setKycStatus(value)}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                  />
                  <Button
                    className={'customer-details-tab-button'}
                    size={'small'}
                    type={'primary'}
                    onClick={() => onSaveKycStatus(kycStatus)}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                  >
                    {i18n.getMessage('customers.corporate.customerDetailsTab.button.save')}
                  </Button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <table className={'table-wrapper'}>
          <tbody className={'table-body'}>
            <tr className={'table-row'}>
              <td colSpan={'2'} className={'table-cell subsection'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.subsection.aml')}
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.corporate.customerDetailsTab.riskProfile')}</td>
              <td className={'table-cell'}>
                <div className={'input-with-save-button-wrapper'}>
                  <InputDropDown
                    className={'table-cell-input'}
                    id={'riskProfile'}
                    name={'riskProfile'}
                    options={riskProfileOptions}
                    value={riskProfile}
                    onChange={(name, value) => setRiskProfile(value)}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                  />
                  <Button
                    className={'customer-details-tab-button'}
                    size={'small'}
                    type={'primary'}
                    onClick={() => onSaveRiskProfile(riskProfile)}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                  >
                    {i18n.getMessage('customers.corporate.customerDetailsTab.button.save')}
                  </Button>
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.nextAmlCheckDate')}
              </td>
              <td className={'table-cell'}>
                <div className={'input-with-save-button-wrapper'}>
                  <CustomDatePicker
                    className={'table-cell-date-input'}
                    name={'nextAMLCheckDate'}
                    value={nextAMLCheckDate}
                    onChange={(name, value) => setNextAMLCheckDate(value)}
                  />
                  <Button
                    className={'customer-details-tab-button'}
                    size={'small'}
                    type={'primary'}
                    onClick={() => onSaveNextAMLCheckDate(convertDate(nextAMLCheckDate))}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                  >
                    {i18n.getMessage('customers.corporate.customerDetailsTab.button.save')}
                  </Button>
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.internalAmlDocumentsLink')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'internalAmlDocumentsLink'}
                    value={values.internalAmlDocumentsLink}
                    onChange={handleChange}
                    error={errors?.internalAmlDocumentsLink || fieldsErrors?.internalAmlDocumentsLink}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.isSanctionsRelated')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <InputDropDown
                    className={'table-cell-input'}
                    id={'sanctionsRelated'}
                    name={'sanctionsRelated'}
                    options={yesOrNoOptions}
                    value={values.sanctionsRelated}
                    onChange={(name, value) => setFieldValue(name, value)}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.isAdverseMediaInvolved')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <InputDropDown
                    className={'table-cell-input'}
                    id={'adverseMediaInvolved'}
                    name={'adverseMediaInvolved'}
                    options={yesOrNoOptions}
                    value={values.adverseMediaInvolved}
                    onChange={(name, value) => setFieldValue(name, value)}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.corporate.customerDetailsTab.mlroApproval')}</td>
              <td className={'table-cell'}>
                <div className={'input-with-save-button-wrapper'}>
                  <InputDropDown
                    className={'table-cell-input'}
                    id={'mlroApproval'}
                    name={'mlroApproval'}
                    options={approvalOptions}
                    value={mlroApproval}
                    onChange={(name, value) => setMlroApproval(value)}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                  />
                  <Button
                    className={'customer-details-tab-button'}
                    size={'small'}
                    type={'primary'}
                    onClick={() => onSaveMlroApproval(mlroApproval)}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                  >
                    {i18n.getMessage('customers.corporate.customerDetailsTab.button.save')}
                  </Button>
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.directorApproval')}
              </td>
              <td className={'table-cell'}>
                <div className={'input-with-save-button-wrapper'}>
                  <InputDropDown
                    className={'table-cell-input'}
                    id={'directorApproval'}
                    name={'directorApproval'}
                    options={approvalOptions}
                    value={directorApproval}
                    onChange={(name, value) => setDirectorApproval(value)}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                  />
                  <Button
                    className={'customer-details-tab-button'}
                    size={'small'}
                    type={'primary'}
                    onClick={() => onSaveDirectorApproval(directorApproval)}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                  >
                    {i18n.getMessage('customers.corporate.customerDetailsTab.button.save')}
                  </Button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* TODO: What is the block and how it need to be display */}
        {/*{(turnovers.length || incomingPartners.length || outgoingPartners.length || !!payment) &&*/}
        {/*  <table className={'table-wrapper'}>*/}
        {/*    <tbody className={'table-body'}>*/}
        {/*      <tr>*/}
        {/*        <td colSpan={"2"} style={{fontWeight: 'bold', fontSize: 'large'}}>*/}
        {/*          {i18n.getMessage('customer.corporate.subsection.kycDataByUser')}*/}
        {/*        </td>*/}
        {/*      </tr>*/}
        {/*    }*/}
        {/*    {*/}
        {/*      turnovers.map(turnover => (*/}
        {/*        <tr key={turnover.type}>*/}
        {/*          <td style={{verticalAlign: 'middle'}}>*/}
        {/*            {i18n.getMessage(`label.ditails.${turnover.type}`)}*/}
        {/*          </td>*/}
        {/*          <td>*/}
        {/*            {`${turnover.value / 100} ${turnover.currency}`}*/}
        {/*          </td>*/}
        {/*        </tr>*/}
        {/*      ))*/}
        {/*    }*/}
        {/*    {*/}
        {/*      incomingPartners.map((partner, index) => (*/}
        {/*        <TableRowPartners*/}
        {/*          key={partner.name}*/}
        {/*          data={partner}*/}
        {/*          type='incoming'*/}
        {/*          index={index + 1}*/}
        {/*        />*/}
        {/*      ))*/}
        {/*    }*/}
        {/*    {*/}
        {/*      outgoingPartners.map((partner, index) => (*/}
        {/*        <TableRowPartners*/}
        {/*          key={partner.name}*/}
        {/*          data={partner}*/}
        {/*          type='outgoing'*/}
        {/*          index={index + 1}*/}
        {/*        />*/}
        {/*      ))*/}
        {/*    }*/}
        {/*    {*/}
        {/*      payment &&*/}
        {/*      <tr>*/}
        {/*        <td style={{verticalAlign: 'middle'}}>*/}
        {/*          Source of the first payment*/}
        {/*        </td>*/}
        {/*        <td>*/}
        {/*          <p>*/}
        {/*            {`${payment.amount / 100} ${payment.currency} ${payment.payer}`}*/}
        {/*          </p>*/}
        {/*          <p>*/}
        {/*            {payment.purpose}*/}
        {/*          </p>*/}
        {/*        </td>*/}
        {/*      </tr>*/}
        {/*    </tbody>*/}
        {/*  </table>*/}
        {/*}*/}
        {/* TODO: What is the block and how it need to be display */}

        {hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE) && (
          <div className={'bottom-actions-wrapper'}>
            <div className={'changes-actions-wrapper'}>
              <Button className={'customers-button'} size={'medium'} type={'outline'} onClick={resetForm}>
                {i18n.getMessage('customers.corporate.customerDetailsTab.button.discard')}
              </Button>
              <Button
                className={'customers-button'}
                roleType={'submit'}
                size={'medium'}
                type={'primary'}
                onClick={() => {
                  validateForm(values).then(() => {
                    scrollToError();
                  });
                }}
              >
                {i18n.getMessage('customers.corporate.customerDetailsTab.button.saveChanges')}
              </Button>
            </div>
          </div>
        )}
      </form>

      <PopUp alignOnCenter show={popUpData.isShowPopUp}>
        <PopConfirmationScheme
          title={popUpData.title}
          description={popUpData.description}
          onConfirmButtonTitle={popUpData.confirmButtonLabel}
          onCloseButtonTitle={popUpData.cancelButtonLabel}
          onConfirm={popUpData.onConfirm}
          onClose={popUpData.onCancel}
        />
      </PopUp>
    </div>
  );
};

CorporateCustomerDetailsTab.propTypes = {
  customerDetails: PropTypes.object.isRequired,
  companyInfo: PropTypes.object.isRequired,
  handleUpdateCustomerDetails: PropTypes.func,
  applicationConstants: PropTypes.object,
  fieldsErrors: PropTypes.any,
  onChangeCustomerStatus: PropTypes.func,
  onChangeOnboardingStatus: PropTypes.func,
  onChangeExternalKycReference: PropTypes.func,
  onReceiveKycData: PropTypes.func.isRequired,
  onSaveKycStatus: PropTypes.func.isRequired,
  onSaveNextAMLCheckDate: PropTypes.func.isRequired,
  onSaveMlroApproval: PropTypes.func.isRequired,
  onSaveDirectorApproval: PropTypes.func.isRequired,
  onSaveRiskProfile: PropTypes.func.isRequired,
  onSaveEmail: PropTypes.func.isRequired,
  parseFullAddress: PropTypes.func.isRequired
};

export default CorporateCustomerDetailsTab;
